'use client';

import type { LinkProps } from 'next/link';
import NextLink from 'next/link';
import type { AnchorHTMLAttributes, MouseEvent as ReactMouseEvent } from 'react';
import { useMemo } from 'react';

import { getEventHasModifierKeys } from '@/lib/util/getEventHasModifierKeys';
import { makePrezlyLinkRelative } from '@/util/seo/makePrezlyLinkRelative';

export interface Props
    extends Omit<LinkProps, 'href' | 'onMouseEnter' | 'onTouchStart' | 'onClick'>,
        Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    href: string;
    onClick?: (event: ReactMouseEvent<Element, MouseEvent>) => void;
    shouldOpenInNewTab?: boolean;
}

/**
 * This component properly handles anchor links, using regular anchor tag instead of `next/link`.
 */
export function Link({
    href = '',
    as,
    replace,
    scroll,
    shallow,
    passHref,
    prefetch,
    locale,
    onClick,
    children,
    shouldOpenInNewTab,
    ...anchorProps
}: Props) {
    const isAnchorLink = Boolean(href.startsWith('#'));
    const isExternalLink = Boolean(href.startsWith('http') || href.startsWith('www'));
    const isTargetBlank =
        shouldOpenInNewTab ?? (isExternalLink && !href.startsWith('https://rock.prezly.com'));

    /** We need a slight delay in navigation to allow time for `onClick` handlers to fire properly */
    const shouldDeferNavigation = Boolean(onClick) && isExternalLink && !isTargetBlank;

    const handleClick = useMemo(
        () => (event: ReactMouseEvent<HTMLAnchorElement, MouseEvent>) => {
            if (!getEventHasModifierKeys(event) && shouldDeferNavigation) {
                event.preventDefault();
            }

            if (onClick) {
                onClick(event);
            }

            if (!getEventHasModifierKeys(event) && shouldDeferNavigation) {
                setTimeout(() => {
                    window.open(href, isTargetBlank ? '_blank' : '_self');
                }, 100);
            }
        },
        [shouldDeferNavigation, onClick, href, isTargetBlank],
    );

    if (!isAnchorLink) {
        return (
            <NextLink
                href={makePrezlyLinkRelative(href)}
                onClick={handleClick}
                target={isTargetBlank ? '_blank' : undefined}
                rel={isTargetBlank ? 'noopener noreferrer' : undefined}
                as={as}
                replace={replace}
                scroll={scroll}
                shallow={shallow}
                prefetch={prefetch}
                locale={locale}
                {...anchorProps}
            >
                {children}
            </NextLink>
        );
    }

    return (
        <a
            href={isAnchorLink ? makePrezlyLinkRelative(href) : undefined}
            target={isTargetBlank ? '_blank' : undefined}
            rel={isTargetBlank ? 'noopener noreferrer' : undefined}
            onClick={onClick}
            {...anchorProps}
        >
            {children}
        </a>
    );
}
